import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useEnvironment } from './EnvironmentContext';

const OperationsContext = createContext(null);
const CONSOLE_API = process.env.REACT_APP_CONSOLE_API_URL
  || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? window.location.origin : 'http://localhost:8097');

function companyIdFromUser(user) {
  const company = user?.activeCompany || user?.myCompany || {};
  return company.id || company.company_id || company.companyId || '';
}

function classifyIncident(order) {
  const status = String(order?.latest_normalized_status || order?.shipment_status || '').toUpperCase();
  if (['DELIVERY_FAILED', 'FAILED', 'ERROR'].includes(status)) return { severity: 'critical', reason: 'Delivery failed' };
  if (['UNKNOWN', 'EXCEPTION', 'RETURNED'].includes(status)) return { severity: 'warning', reason: status.toLowerCase().replaceAll('_', ' ') };
  const lastSeen = new Date(order?.latest_status_at || order?.updated_at || 0).getTime();
  const terminal = ['DELIVERED', 'CANCELLED'].includes(status);
  if (!terminal && lastSeen && Date.now() - lastSeen > 48 * 60 * 60 * 1000) return { severity: 'warning', reason: 'Tracking is stale' };
  return null;
}

export function OperationsProvider({ children }) {
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [carrierReports, setCarrierReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [incidentDrawerOpen, setIncidentDrawerOpen] = useState(false);
  const [period, setPeriod] = useState('7d');
  const companyId = companyIdFromUser(user);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    const companyParam = companyId ? `&company_id=${encodeURIComponent(companyId)}` : '';
    try {
      const [dashboardResponse, ordersResponse, carriersResponse] = await Promise.all([
        fetch(`${CONSOLE_API}/v1/dashboard?environment=${environment}&period=${period}`, { headers: { 'X-Environment': environment } }),
        fetch(`${CONSOLE_API}/v1/orders?limit=200${companyParam}`, { headers: { 'X-Environment': environment } }),
        fetch(`${CONSOLE_API}/v1/reports/carriers?environment=${environment}&period=${period}`, { headers: { 'X-Environment': environment } }),
      ]);
      if (!dashboardResponse.ok) throw new Error(`Operations request failed (${dashboardResponse.status})`);
      setDashboard(await dashboardResponse.json());
      const ordersData = ordersResponse.ok ? await ordersResponse.json() : [];
      const carriersData = carriersResponse.ok ? await carriersResponse.json() : [];
      setOrders(Array.isArray(ordersData) ? ordersData : ordersData?.items || []);
      setCarrierReports(Array.isArray(carriersData) ? carriersData : carriersData?.items || []);
      setUpdatedAt(new Date());
    } catch (requestError) {
      setError(requestError.message || 'Operational data is unavailable');
    } finally {
      setLoading(false);
    }
  }, [companyId, environment, period, user]);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const incidents = useMemo(() => orders.map((order) => {
    const incident = classifyIncident(order);
    return incident ? { ...incident, order } : null;
  }).filter(Boolean), [orders]);

  const value = useMemo(() => ({
    dashboard, orders, carrierReports, incidents, loading, error, updatedAt, refresh,
    incidentDrawerOpen, setIncidentDrawerOpen, period, setPeriod,
  }), [dashboard, orders, carrierReports, incidents, loading, error, updatedAt, refresh, incidentDrawerOpen, period]);

  return <OperationsContext.Provider value={value}>{children}</OperationsContext.Provider>;
}

export function useOperations() {
  const value = useContext(OperationsContext);
  if (!value) throw new Error('useOperations must be used inside OperationsProvider');
  return value;
}
